// Quyết định làm gì với TỪNG workspace trong registry khi chạy `avatar sync --all` (#211 phase-02).
//
// Hàm THUẦN, không I/O, không mạng: mọi dữ kiện đọc từ đĩa (marker, version pack, mtime,
// phiên Claude Code) do caller gom sẵn và truyền vào. Nhờ vậy 5 luật lọc + positive control
// test được bằng unit test, không cần dựng workspace thật hay chạm Supabase.
//
// ═══ THỨ TỰ 2 TRỤC LÀ RÀNG BUỘC, KHÔNG PHẢI CÁCH TRÌNH BÀY ═══
//
//   TRỤC AN TOÀN (áp cho MỌI entry, kể cả version không đọc được):
//     marker mất            → pruned
//     mốc hoạt động quá hạn → skipped:stale
//     phiên Claude Code sống→ skipped:open-session
//   TRỤC VERSION (chỉ áp khi CẢ HAI phía parse được):
//     bằng đích             → skipped:same-version  (trừ khi `reinstall` ⇒ will-sync)
//     mới hơn đích          → skipped:newer-installed   (#191)
//     còn lại / không parse → will-sync
//
// Vì sao thứ tự này bắt buộc: `readPackVersionFromFile` trả `null` khi thiếu cả manifest lẫn
// `.pack-version` (`pack-manifest-store.ts:43-53`) — ca RẤT thường (workspace vừa init chưa
// sync, pack bị xoá/hỏng). Để nhánh "không parse được ⇒ will-sync" đứng TRƯỚC thì nó
// short-circuit: workspace chưa cài pack sẽ bị sync DÙ đang có phiên Claude Code sống (pack bị
// `atomicSwapDir` rút dưới chân) và DÙ đã stale.
import {
  classifyPackVersionTransition,
  type PackVersionTransition,
} from "./compare-pack-semver-and-detect-downgrade.js";
import { decideNonDevWorkspaceSkip } from "./decide-non-dev-workspace-skip-for-sync-all.js";
import type { ClaudeSessionDetection } from "./detect-open-claude-code-session.js";
import type { WorkspaceEntry } from "./workspace-registry-store.js";

/** Mặc định 90 ngày — plan.md §Q1 (Luke chốt 2026-08-20). */
export const DEFAULT_STALE_DAYS = 90;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export type SkipReason =
  | "same-version"
  | "newer-installed"
  | "stale"
  | "open-session"
  | "managed-mission"
  | "non-dev-workspace";

export type WorkspaceDecision =
  | { kind: "will-sync"; path: string; currentVersion: string | null }
  | {
      kind: "skipped";
      path: string;
      currentVersion: string | null;
      reason: SkipReason;
      /** Câu giải thích cho bảng tổng kết (vd bằng chứng phiên đang mở). */
      detail: string;
    }
  | { kind: "pruned"; path: string; currentVersion: null };

/** Dữ kiện đọc từ đĩa cho MỘT entry registry. Caller (tầng I/O) gom, hàm này chỉ suy luận. */
export interface WorkspaceFacts {
  entry: WorkspaceEntry;
  /** `.claude/avatar.json` còn tồn tại tại `entry.path` không. */
  markerExists: boolean;
  /** Version pack đang cài, THÔ (`v1.1.67`, `local-2026-08-20`, …) — `null` khi không đọc nổi. */
  currentVersion: string | null;
  /** mtime `.claude/pack` (ms epoch) — `null` khi thư mục không có / không stat được. */
  packMtimeMs: number | null;
  session: ClaudeSessionDetection;
  /** `kind` trong marker (#254). Vắng = "dev" — giữ nguyên quyết định cho caller cũ. */
  workspaceKind?: string;
}

export interface PlanSyncAllOptions {
  /** Version ĐÍCH, thô. Không parse được (vd `local-<date>`) ⇒ 2 luật version tự tắt. */
  targetVersion: string;
  now: number;
  staleDays?: number;
  /** `--include-downgrades`: cho phép hạ cấp workspace đang ở bản mới hơn đích. */
  includeDowngrades?: boolean;
  /**
   * `--force-open-sessions <path>` (lặp được). CHỈ tắt luật open-session cho đúng những
   * path nêu ra — dạng boolean sẽ biến một xác nhận phạm vi 1-workspace của extension
   * thành override phạm vi TOÀN MÁY. Path phải đã `resolve` bởi caller.
   */
  forceOpenSessionPaths?: readonly string[];
  /**
   * `--reinstall` (force update 13/10): workspace đã ở đúng bản đích vẫn `will-sync` để con
   * tải lại. CHỈ tắt luật `same-version` — trục an toàn và `newer-installed` giữ nguyên.
   */
  reinstall?: boolean;
}

/**
 * Mốc hoạt động mới nhất của workspace = `max` của các mốc CÓ MẶT.
 *
 * ⚠️ `lastSeenAt` MỘT MÌNH là sai persona: nó chỉ nhích ở 4 call site CLI
 * (`init`/`sync`/`status`/`doctor`), mà persona của chính ticket là người KHÔNG gõ terminal
 * ⇒ workspace dùng hằng ngày trong VSCode sẽ bị `skipped:stale`. Vì vậy mốc #2 (mtime
 * transcript Claude Code) và mốc #3 (mtime `.claude/pack`) là bắt buộc.
 *
 * ⚠️ Mốc thiếu ⇒ BỎ khỏi `max`, KHÔNG quy về 0. Hôm nay `null → 0` chỉ vô hại nhờ `max`;
 * đổi sang `min`/trung bình là hỏng ngay. Khai luật ở đây cho khỏi trôi.
 */
export function newestActivityMs(facts: WorkspaceFacts): number | null {
  const lastSeen = Date.parse(facts.entry.lastSeenAt);
  const marks = [
    Number.isNaN(lastSeen) ? null : lastSeen,
    facts.session.newestMtimeMs,
    facts.packMtimeMs,
  ].filter((m): m is number => m !== null);
  return marks.length === 0 ? null : Math.max(...marks);
}

function isStale(facts: WorkspaceFacts, now: number, staleDays: number): boolean {
  const newest = newestActivityMs(facts);
  // Không có mốc nào ⇒ KHÔNG kết luận stale: thiếu bằng chứng không phải bằng chứng.
  if (newest === null) return false;
  return now - newest > staleDays * MS_PER_DAY;
}

function describeStale(facts: WorkspaceFacts, now: number): string {
  const newest = newestActivityMs(facts);
  if (newest === null) return "không có mốc hoạt động nào";
  return `hoạt động gần nhất ${Math.floor((now - newest) / MS_PER_DAY)} ngày trước`;
}

/** Quyết định cho MỘT workspace. Tách riêng để unit test đọc được từng luật. */
export function decideForWorkspace(
  facts: WorkspaceFacts,
  opts: PlanSyncAllOptions,
): WorkspaceDecision {
  const path = facts.entry.path;
  const staleDays = opts.staleDays ?? DEFAULT_STALE_DAYS;

  // ── Trục an toàn ────────────────────────────────────────────────────────────
  if (!facts.markerExists) return { kind: "pruned", path, currentVersion: null };

  const currentVersion = facts.currentVersion;
  const nonDev = decideNonDevWorkspaceSkip(facts);
  if (nonDev !== null) return nonDev;

  // Mission workspace (§9.4, B35): pack phải bất biến suốt mission — đứng TRƯỚC
  // stale/open-session vì cả hai luật đó vẫn có thể true trên workspace đang chạy
  // mission (ví dụ không phiên Claude Code nào mở giữa các attempt) mà pack KHÔNG
  // được đổi. Additive: entry không có cờ `managedMission` ⇒ bỏ qua nhánh này,
  // rơi thẳng xuống luật cũ — quyết định y hệt trước khi có nhánh này.
  if (facts.entry.managedMission === true) {
    return {
      kind: "skipped",
      path,
      currentVersion,
      reason: "managed-mission",
      detail: `mission workspace (missionId=${facts.entry.missionId ?? "?"}) — pack bất biến suốt mission`,
    };
  }

  if (isStale(facts, opts.now, staleDays)) {
    return {
      kind: "skipped",
      path,
      currentVersion,
      reason: "stale",
      detail: `${describeStale(facts, opts.now)} (ngưỡng ${staleDays} ngày)`,
    };
  }

  const forced = (opts.forceOpenSessionPaths ?? []).includes(path);
  if (facts.session.status === "open" && !forced) {
    return {
      kind: "skipped",
      path,
      currentVersion,
      reason: "open-session",
      detail: facts.session.evidence,
    };
  }

  // ── Trục version ────────────────────────────────────────────────────────────
  // `unknown` = ÍT NHẤT một phía không parse được (chưa cài pack, `local-<date>`, `+build`,
  // rỗng, null). Không so chuỗi, không suy `newer-installed` (chặn oan) cũng không suy
  // `same-version` (bỏ sót) — chỉ đơn giản là không có căn cứ ⇒ để sync quyết.
  const transition: PackVersionTransition = classifyPackVersionTransition(
    currentVersion,
    opts.targetVersion,
  );

  if (transition === "noop" && opts.reinstall !== true) {
    return {
      kind: "skipped",
      path,
      currentVersion,
      reason: "same-version",
      detail: `đã ở đúng bản đích ${opts.targetVersion}`,
    };
  }

  if (transition === "downgrade" && opts.includeDowngrades !== true) {
    return {
      kind: "skipped",
      path,
      currentVersion,
      reason: "newer-installed",
      detail: `bản đang cài MỚI HƠN đích ${opts.targetVersion}`,
    };
  }

  return { kind: "will-sync", path, currentVersion };
}

/** Quyết định cho cả registry, giữ nguyên thứ tự entry đầu vào. */
export function planSyncAllAcrossRegisteredWorkspaces(
  facts: readonly WorkspaceFacts[],
  opts: PlanSyncAllOptions,
): WorkspaceDecision[] {
  return facts.map((f) => decideForWorkspace(f, opts));
}
